import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  LayoutDashboard,
  FileText,
  Briefcase,
  Users,
  Building2,
  DollarSign,
  Scale,
  Home,
  Shield,
  Settings,
  Image,
  FolderOpen,
  FileCode,
  BarChart3,
  BookOpen,
  ClipboardList,
  ChevronDown,
  Contact,
  Download,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  closeSidebar: () => void;
}

interface MenuItem {
  path: string;
  icon: any;
  label: string;
}

interface MenuGroup {
  label: string;
  icon: any;
  basePath: string;
  children: MenuItem[];
}

type SidebarItem = MenuItem | MenuGroup;

function isGroup(item: SidebarItem): item is MenuGroup {
  return 'children' in item;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, closeSidebar }) => {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const isActive = (path: string) => location.pathname.startsWith(path);

  const isGroupActive = (group: MenuGroup) =>
    group.children.some((child) => isActive(child.path));

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isGroupOpen = (group: MenuGroup) =>
    openGroups[group.label] !== undefined ? openGroups[group.label] : isGroupActive(group);

  const sidebarItems: SidebarItem[] = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/news', icon: FileText, label: 'News' },
    { path: '/government-orders', icon: BookOpen, label: 'Government Orders' },
    { path: '/tenders', icon: Briefcase, label: 'Tenders' },
    {
      label: 'Documents',
      icon: FolderOpen,
      basePath: '/documents',
      children: [
        { path: '/documents/bt_rates', icon: FolderOpen, label: 'BT Rates' },
        { path: '/documents/ssr', icon: FolderOpen, label: 'SSR' },
        { path: '/documents/ssr_emd_tax_rates', icon: FolderOpen, label: 'SSR EMD Tax Rates' },
        { path: '/documents/ssr_final_sors', icon: FolderOpen, label: 'SSR Final SORs' },
        { path: '/documents/achievements', icon: FolderOpen, label: 'Achievements' },
      ],
    },
    { path: '/downloads', icon: Download, label: 'Downloads' },
    { path: '/steel-cement-rates', icon: DollarSign, label: 'Steel & Cement Rates' },
    { path: '/gallery', icon: Image, label: 'Gallery' },
    {
      label: 'Organization',
      icon: Building2,
      basePath: '/org',
      children: [
        { path: '/contacts', icon: Users, label: 'Contacts' },
        { path: '/circles-divisions', icon: Building2, label: 'Circles & Divisions' },
        { path: '/administration', icon: Shield, label: 'Administration' },
        { path: '/rti-officers', icon: Scale, label: 'RTI Officers' },
      ],
    },
    {
      label: 'Pages',
      icon: FileCode,
      basePath: '/pages',
      children: [
        { path: '/homepage-content', icon: Home, label: 'Homepage Content' },
        { path: '/citizen-charter', icon: ClipboardList, label: 'Citizen Charter' },
        { path: '/contact-us-page', icon: Contact, label: 'Contact Us' },
        { path: '/assets-management', icon: BarChart3, label: 'Assets' },
        { path: '/static-pages', icon: FileCode, label: 'Static Pages' },
      ],
    },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="lg:hidden absolute top-4 right-4">
          <button
            onClick={closeSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto h-full pb-20">
          {sidebarItems.map((item) => {
            if (isGroup(item)) {
              const groupActive = isGroupActive(item);
              const open = isGroupOpen(item);

              return (
                <div key={item.label}>
                  <button
                    onClick={() => toggleGroup(item.label)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                      groupActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {open && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-100 pl-2">
                      {item.children.map((child) => {
                        const active = isActive(child.path);
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={closeSidebar}
                            className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              active
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <span>{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  active
                    ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          <div className="text-xs text-gray-500 text-center">
            <p>PRED Admin CMS</p>
            <p className="mt-1">Version 1.0.0</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
